import { BindingError } from "../BindingError.js";
import { EventManager } from "./EventManager.js";
import { OperationsHelpers } from "./operations/OperationsHelpers.js";
import { assert } from "../utils/assert.js";
import { ServerId, UnpersistedEntityDummyId, ClientGeneratedUuid } from "../accessorTree/RuntimeId.js";
import { assertNever } from "../utils/assertNever.js";
class TreeAugmenter {
  constructor(eventManager, stateInitializer, treeStore, skipStateUpdateAfterPersist = false) {
    this.eventManager = eventManager;
    this.stateInitializer = stateInitializer;
    this.treeStore = treeStore;
    this.skipStateUpdateAfterPersist = skipStateUpdateAfterPersist;
  }
  extendPersistedData(newPersistedData, markerTree) {
    this.treeStore.mergeInQueryResponse(newPersistedData, markerTree);
  }
  extendTreeStates(newTreeId, newMarkerTree) {
    const subTreeStates = /* @__PURE__ */ new Map();
    this.treeStore.markerTrees.set(newTreeId, newMarkerTree);
    this.treeStore.subTreeStatesByRoot.set(newTreeId, subTreeStates);
    for (const [placeholderName, newSubTreeMarker] of newMarkerTree.subTrees) {
      const newState = this.stateInitializer.initializeSubTree(newSubTreeMarker);
      subTreeStates.set(placeholderName, newState);
    }
  }
  updatePersistedData(response, operations) {
    this.treeStore.mergeInMutationResponse(response, operations);
    if (this.skipStateUpdateAfterPersist) {
      return;
    }
    this.eventManager.syncTransaction(() => {
      for (const rootStates of this.treeStore.subTreeStatesByRoot.values()) {
        for (const [rootPlaceholder, rootState] of rootStates) {
          const rootData = this.treeStore.subTreePersistedData.get(rootPlaceholder);
          if (rootState.type === "entityList") {
            if (!(rootData instanceof Set)) {
              continue;
            }
            this.updateEntityListPersistedData(rootState, rootData);
          } else if (rootState.type === "entityRealm") {
            if (rootData !== void 0 && !(rootData instanceof ServerId)) {
              continue;
            }
            this.updateRealmIdIfNecessary(rootState, rootData);
            this.updateEntityRealmPersistedData(rootState);
          } else {
            return assertNever();
          }
        }
      }
    });
  }
  resetCreatingSubTrees() {
    for (const rootStates of this.treeStore.subTreeStatesByRoot.values()) {
      for (const [rootPlaceholder, rootState] of rootStates) {
        if (rootState.type === "entityList") {
          assert(rootState.blueprint.parent === void 0);
          if (!rootState.blueprint.marker.parameters.isCreating) {
            continue;
          }
          this.treeStore.subTreePersistedData.delete(rootPlaceholder);
          const changesCount = rootState.unpersistedChangesCount;
          for (const childState of rootState.children.values()) {
            this.treeStore.disposeOfRealm(childState);
          }
          rootState.accessor = void 0;
          rootState.children.clear();
          rootState.childrenWithPendingUpdates?.clear();
          rootState.errors = void 0;
          rootState.plannedRemovals?.clear();
          this.eventManager.registerJustUpdated(
            rootState,
            changesCount ? -1 * changesCount : EventManager.NO_CHANGES_DIFFERENCE
          );
        } else if (rootState.type === "entityRealm") {
          assert(rootState.blueprint.parent === void 0);
          if (!rootState.blueprint.marker.parameters.isCreating) {
            continue;
          }
          const presentListeners = rootState.eventListeners;
          this.treeStore.disposeOfRealm(rootState);
          this.treeStore.subTreePersistedData.delete(rootPlaceholder);
          const newRootState = this.stateInitializer.initializeSubTree(rootState.blueprint.marker);
          newRootState.eventListeners = presentListeners;
          OperationsHelpers.purgeStaleListenersAfterIdChange(newRootState);
          rootStates.set(rootPlaceholder, newRootState);
          this.eventManager.registerJustUpdated(newRootState, EventManager.NO_CHANGES_DIFFERENCE);
        } else {
          assertNever();
        }
      }
    }
  }
  updateRealmIdIfNecessary(realm, newId) {
    const currentId = realm.entity.id;
    let idToChangeTo = void 0;
    if (currentId instanceof ServerId) {
      if (newId === void 0 || newId === null) {
        idToChangeTo = new UnpersistedEntityDummyId();
      } else if (currentId.value !== newId.value) {
        idToChangeTo = newId;
      }
    } else if (currentId instanceof ClientGeneratedUuid) {
      idToChangeTo = newId ?? new UnpersistedEntityDummyId();
    } else if (currentId instanceof UnpersistedEntityDummyId) {
      if (newId) {
        idToChangeTo = newId;
      }
    } else {
      return assertNever();
    }
    if (idToChangeTo) {
      OperationsHelpers.changeRealmId(this.treeStore, this.eventManager, this.stateInitializer, realm, idToChangeTo);
    }
  }
  updateEntityRealmPersistedData(realm) {
    const realmId = realm.entity.id;
    const persistedData = realmId.existsOnServer ? this.treeStore.persistedEntityData.get(realmId.uniqueValue) : void 0;
    for (const [placeholderName, child] of realm.children) {
      const childData = persistedData?.get(placeholderName);
      switch (child.type) {
        case "field": {
          if (childData instanceof ServerId || childData instanceof Set) {
            throw new BindingError();
          }
          if (child.persistedValue !== childData) {
            const shouldChangeBothValues = child.persistedValue === child.value;
            child.persistedValue = childData;
            if (shouldChangeBothValues) {
              child.value = childData ?? null;
            }
            this.eventManager.registerJustUpdated(child, EventManager.NO_CHANGES_DIFFERENCE);
          }
          break;
        }
        case "entityRealm":
        case "entityRealmStub": {
          if (!(childData instanceof ServerId) && childData !== void 0 && childData !== null) {
            throw new BindingError();
          }
          this.updateRealmIdIfNecessary(child, childData);
          if (child.type === "entityRealm") {
            this.updateEntityRealmPersistedData(child);
          }
          break;
        }
        case "entityList": {
          if (childData !== void 0 && !(childData instanceof Set)) {
            throw new BindingError();
          }
          this.updateEntityListPersistedData(child, childData ?? /* @__PURE__ */ new Set());
          break;
        }
        default: {
          return assertNever();
        }
      }
    }
  }
  updateEntityListPersistedData(state, newPersistedIds) {
    const persistedWithoutState = [];
    for (const persistedId of newPersistedIds) {
      if (!state.children.has(persistedId)) {
        persistedWithoutState.push(persistedId);
      }
    }
    for (const [stateId, child] of state.children) {
      const childRuntimeId = child.entity.id;
      if (!newPersistedIds.has(stateId)) {
        if (childRuntimeId instanceof ServerId || childRuntimeId instanceof ClientGeneratedUuid || persistedWithoutState.length === 0) {
          state.getAccessor().disconnectEntity(child.getAccessor());
          continue;
        } else {
          const drawnId = persistedWithoutState.shift();
          OperationsHelpers.changeRealmId(
            this.treeStore,
            this.eventManager,
            this.stateInitializer,
            child,
            new ServerId(drawnId, child.entity.entityName)
          );
        }
      } else if (childRuntimeId instanceof ClientGeneratedUuid) {
        OperationsHelpers.changeRealmId(
          this.treeStore,
          this.eventManager,
          this.stateInitializer,
          child,
          new ServerId(childRuntimeId.value, child.entity.entityName)
        );
      }
      if (child.type === "entityRealm") {
        this.updateEntityRealmPersistedData(child);
      }
    }
    for (const unclaimedPersistedId of persistedWithoutState) {
      this.stateInitializer.initializeEntityRealm(new ServerId(unclaimedPersistedId, state.entityName), state.entityName, {
        type: "listEntity",
        parent: state
      });
    }
    state.children.changeKeyOrder(newPersistedIds);
  }
}
export {
  TreeAugmenter
};
//# sourceMappingURL=TreeAugmenter.js.map
